// The map and the train canvas over it: the basemap's key and style (each
// fetched within a deadline), the map opened on the city and fitted to what
// the panel leaves visible, its credits, the renderer, the frame loop that
// moves and draws the trains, resizing, and the pause while the tab is
// hidden.

import { AttributionControl, Map as MapLibreMap, NavigationControl } from 'maplibre-gl';
import type { StyleSpecification } from 'maplibre-gl';
import * as Core from '@tol/core/core';
import {
  CARTO_STYLE,
  EMPTY_STYLE,
  BASEMAP_CREDIT,
  TEXT_SCALE,
  basemapStyle,
  cartoTransform,
  isStyle,
  updateNetworkTextSize,
} from '@tol/core/layers';
import * as Render from '@tol/core/render';
import type { Renderer } from '@tol/core/render';
import { ZOOM_OFFSET, mapView } from '@tol/core/view';
import type { MapView } from '@tol/core/view';
import { VIEW_MARGIN } from '@tol/core/onscreen';
import type { LatLon, LineId, PageLine, Scene, TrackedTrain, TrainId, TrainStore } from '@tol/core/types';
import { el } from './dom.ts';
import { advanceGpsTrains } from './gps-motion.ts';
import { installMapCanvasLayer, mapCanvasLayer } from './map-canvas-layer.ts';
import type { ShellOptions } from './shell.ts';
import type { ShellState } from './state.ts';

// The map waits for the config: a request that stalls is given up after
// this long (ms), and the map drawn without the basemap.
const START_FETCH_MS = 6000;

// A URL's JSON, or null for an error status; throws once `ms` pass
// without the whole body.
async function jsonWithin(url: string, ms: number): Promise<unknown> {
  const deadline = new AbortController();
  const timer = setTimeout(() => {
    deadline.abort(new Error(`${url}: no answer in ${ms} ms`));
  }, ms);
  try {
    const res = await fetch(url, { signal: deadline.signal });
    return res.ok ? await res.json() : null;
  } finally {
    clearTimeout(timer);
  }
}

// The page's config: in the page (the server's #page-config), else asked
// for.
function inlineConfig(): unknown {
  const text = document.querySelector('#page-config')?.textContent ?? '';
  if (text === '') return undefined;
  try {
    const value: unknown = JSON.parse(text);
    return value;
  } catch {
    return undefined;
  }
}

async function cartoKey(): Promise<string | null> {
  const inline = inlineConfig();
  if (typeof inline === 'object' && inline !== null && 'cartoKey' in inline) {
    return typeof inline.cartoKey === 'string' && inline.cartoKey !== '' ? inline.cartoKey : null;
  }
  try {
    // Relative, like every URL the page uses: the page works at a domain's
    // root and under a path (transitof.com/lon/).
    const config = await jsonWithin('config.json', START_FETCH_MS);
    if (typeof config !== 'object' || config === null || !('cartoKey' in config)) return null;
    return typeof config.cartoKey === 'string' && config.cartoKey !== '' ? config.cartoKey : null;
  } catch {
    return null;
  }
}

// The map's text size for the current screen (TEXT_SCALE).
export const textScale = (): number =>
  window.matchMedia('(max-width: 680px)').matches ? TEXT_SCALE.phone : TEXT_SCALE.desktop;

function loadStyle(key: string | null): StyleSpecification {
  return key !== null && isStyle(CARTO_STYLE) ? CARTO_STYLE : EMPTY_STYLE;
}

// Besides the city's view, credits, keepRight and hidden/visible hooks
// (ShellOptions), what the map and its frames need.
export interface MapFrameInputs {
  readonly lines: Readonly<Partial<Record<LineId, PageLine>>>;
  readonly debug: boolean;
  // ?z=: an opening zoom to try, in Leaflet zooms.
  readonly zoom: string | null;
  // What the renderer draws: the network, the shown lines, the selection.
  readonly state: ShellState;
  readonly live: TrainStore;
  readonly now: () => number;
  // The city's own trains, drawn on the train canvas after the shell's.
  readonly drawExtra:
    | ((ctx: CanvasRenderingContext2D, dt: number, scale: number, width: number, height: number) => void)
    | undefined;
  // Whether that extra layer put moving vehicles in its last draw.
  readonly extraMoving: () => boolean;
  // After a draw that moved the trains (the glitch watcher).
  readonly onMotion: () => void;
  // Each frame after the trains moved and were drawn (Follow).
  readonly onFrame: (ts: number) => void;
  // Trains the motion model dropped.
  readonly onRemoved: (removed: readonly TrainId[]) => void;
  // Where a followed selection is, to resume there.
  readonly followed: () => LatLon | null;
}

export interface MapFrame {
  readonly map: MapLibreMap;
  readonly view: MapView;
  readonly renderer: Renderer;
  // Whether the network layers' basemap is drawn.
  readonly hasBasemap: boolean;
  // Draw the trains with the camera as it is now. dt: ms of motion since the
  // last draw (0: none, just a redraw).
  readonly draw: (dt?: number) => void;
  // Add the train compositor after the map's ordinary network layers.
  readonly install: () => void;
  // Start the frame loop (once).
  readonly start: () => void;
  readonly running: () => boolean;
  // Resets the motion model at the next frame.
  readonly resetMotion: () => void;
}

export async function createMapFrame(city: ShellOptions, options: MapFrameInputs): Promise<MapFrame> {
  const { state, live } = options;
  const phoneQuery = window.matchMedia('(max-width: 680px)');
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  const key = await cartoKey();
  const style = loadStyle(key);
  const hasBasemap = key !== null && style !== EMPTY_STYLE;
  const phone = phoneQuery.matches;

  // Opens on the city's center, fitted to whatever the panel leaves visible
  // (a strip along the bottom on phones, a column on the left otherwise).
  const panelBox = el('#panel').getBoundingClientRect();
  const map = new MapLibreMap({
    container: 'map',
    pixelRatio: Math.min(window.devicePixelRatio || 1, Render.MAX_PIXEL_RATIO),
    style: hasBasemap ? basemapStyle(style, textScale()) : style,
    ...(key === null ? {} : { transformRequest: cartoTransform(key) }),
    bounds: phone ? city.view.bounds.phone : city.view.bounds.desktop,
    fitBoundsOptions: {
      maxZoom: 14 - ZOOM_OFFSET,
      padding: {
        top: 0,
        right: 0,
        left: phone ? 0 : panelBox.right,
        bottom: phone ? Math.max(0, window.innerHeight - panelBox.top) : 0,
      },
    },
    minZoom: city.view.minZoom - ZOOM_OFFSET,
    maxZoom: city.view.maxZoom - ZOOM_OFFSET,
    maxBounds: city.view.maxBounds,
    // The train canvas assumes a north-up, flat map (see view.ts).
    dragRotate: false,
    pitchWithRotate: false,
    touchPitch: false,
    rollEnabled: false,
    renderWorldCopies: false,
    // Zero forces a full synchronous label placement on every map frame.
    // Reserve it for reduced motion so normal pinches retain incremental placement.
    fadeDuration: motionQuery.matches ? 0 : 300,
    attributionControl: false,
  });
  map.touchZoomRotate.disableRotation();
  map.keyboard.disableRotation();
  {
    // Phones: 13.5 (Leaflet zoom) had the most visibly moving trains in an
    // iPhone sweep of 12.5 to 14. ?z=13 tries another opening zoom (in
    // Leaflet zooms, like everything the renderer sizes).
    const requestedZoom = Number(options.zoom);
    const z =
      (Number.isFinite(requestedZoom) ? requestedZoom : 0) || (phone && city.view.phoneFits !== true ? 13.5 : 0);
    if (z) map.jumpTo({ zoom: z - ZOOM_OFFSET });
  }

  // CARTO requires a visible basemap credit (https://carto.com/attribution/).
  // Keep the existing fold instead of MapLibre's compact control, which hides
  // every credit and starts expanded. All screen sizes fold the remaining
  // credits behind an (i); MapLibre still collects every visible source's.
  // CARTO's credit is the basemap source's (basemapStyle).
  map.addControl(
    new AttributionControl({
      compact: false,
      customAttribution: ['<a href="https://maplibre.org/">MapLibre</a>', ...city.credits],
    }),
    'bottom-right',
  );
  // Bottom controls stack upward: the zoom buttons go above the attribution.
  map.addControl(new NavigationControl({ showCompass: false }), 'bottom-right');
  const attribution = map.getContainer().querySelector('.maplibregl-ctrl-attrib');
  if (attribution instanceof HTMLElement) {
    const basemap = document.createElement('span');
    basemap.className = 'attrib-basemap';
    basemap.innerHTML = BASEMAP_CREDIT;
    const more = document.createElement('button');
    more.type = 'button';
    more.className = 'attrib-more';
    more.setAttribute('aria-expanded', 'false');
    more.setAttribute('aria-label', 'All credits');
    more.addEventListener('click', () => {
      const open = attribution.classList.toggle('attrib-open');
      more.setAttribute('aria-expanded', String(open));
    });
    attribution.prepend(more);
    if (hasBasemap) attribution.prepend(basemap);
    new ResizeObserver(() => {
      document.documentElement.style.setProperty('--attribution-height', `${attribution.offsetHeight}px`);
    }).observe(attribution);
  }

  if (!hasBasemap) {
    const recovery = document.createElement('button');
    recovery.type = 'button';
    recovery.className = 'map-retry';
    recovery.textContent = 'Basemap unavailable · Reload to retry map';
    recovery.addEventListener('click', () => {
      location.reload();
    });
    map.getContainer().append(recovery);
  }

  // ---------------------------------------------------------------- trains canvas

  const view = mapView(map);
  // Detached: its pixels are uploaded by a MapLibre custom layer below, so
  // Safari cannot present trains from a different camera frame than the map.
  const trainCanvas = document.createElement('canvas');
  const trainCtx = trainCanvas.getContext('2d');

  function scene(): Scene {
    const sel = state.selected;
    return {
      net: state.net,
      trains: live.trains,
      layouts: state.layouts,
      hidden: state.hidden,
      focus: state.focus,
      selectedId: sel && sel.type === 'train' ? sel.id : null,
      visibleStations: state.visibleStations,
    };
  }

  const renderer = Render.create({
    map: view,
    lines: options.lines,
    trainCanvas,
    scene,
    // MapLibre draws the station dots; the renderer rings stopped trains' stations.
    stationDots: false,
    keepRight: city.keepRight ?? false,
    get reduceMotion() {
      return motionQuery.matches;
    },
    debug: options.debug,
  });

  function paint(dt = 0): void {
    view.sync();
    renderer.draw(dt);
    if (options.drawExtra && trainCtx) {
      const { x: width, y: height } = view.getSize();
      options.drawExtra(trainCtx, dt, Render.trainScale(view.getZoom()), width, height);
    }
    if (dt > 0) options.onMotion();
  }

  // Public redraws invalidate the MapLibre frame. The custom layer paints
  // later, against the exact camera MapLibre is about to present.
  function draw(_dt?: number): void {
    map.triggerRepaint();
  }

  renderer.resize();
  map.on('resize', () => {
    view.sync();
    renderer.resize();
    draw(0);
  });
  function updateTextSize(): void {
    if (hasBasemap) {
      for (const layer of basemapStyle(style, textScale()).layers) {
        if (layer.type === 'symbol' && layer.layout?.['text-size'] !== undefined && map.getLayer(layer.id)) {
          map.setLayoutProperty(layer.id, 'text-size', layer.layout['text-size']);
        }
      }
      updateNetworkTextSize(map, textScale(), phoneQuery.matches);
    }
  }
  phoneQuery.addEventListener('change', updateTextSize);
  map.once('load', updateTextSize);
  motionQuery.addEventListener('change', () => {
    // MapLibre exposes no setter; this is the field its renderer and tile
    // manager read. Keep live preference changes without recreating the map.
    map._fadeDuration = motionQuery.matches ? 0 : 300;
    if (motionQuery.matches) map.stop();
    draw(0);
  });
  // ---------------------------------------------------------------- frames

  let running = false;
  let installed = false;
  let lastFrame = performance.now();
  let resetAnimation = false;
  let paintedAt: number | null = null;

  const trainLayer = mapCanvasLayer('train-canvas', trainCanvas, () => {
    // Custom ratios stop following DPR; display changes may keep the same CSS
    // size. setPixelRatio fires resize, which updates this detached canvas.
    const ratio = Math.min(window.devicePixelRatio || 1, Render.MAX_PIXEL_RATIO);
    if (map.getPixelRatio() !== ratio) map.setPixelRatio(ratio);
    const ts = performance.now();
    const elapsed = ts - lastFrame;
    const dt = Math.min(elapsed, 250);
    lastFrame = ts;
    if (running && state.net && !document.hidden) {
      const reset = resetAnimation || elapsed > 1000;
      advance(state.net, dt, reset);
      resetAnimation = false;
      paint(reset ? 0 : dt);
      paintedAt = ts;
      // Animated trains ask MapLibre for another shared render frame. An
      // empty world view stops here instead of uploading two full-screen
      // canvases forever; a data update requests the first new frame.
      if (live.trains.size > 0 || options.extraMoving()) map.triggerRepaint();
    } else {
      paint(0);
    }
    return 'upload';
  });

  // Follow runs after the layer and map were painted. Keep it out of the
  // custom render callback because it can change the camera for the next frame.
  map.on('render', () => {
    const ts = paintedAt;
    if (ts === null) return;
    paintedAt = null;
    options.onFrame(ts);
  });

  // Whether a train may be drawn this frame (Core.advanceTrains), from its
  // last position, `meters` from where it would be now: the renderer places
  // trains within CULL px of the canvas, and the panel counts those in the
  // view grown by VIEW_MARGIN (onscreen.ts; twice that here, for Mercator's
  // stretch). The selected train moves wherever it is, for its card and
  // Follow. Only on the north-up, flat map the renderer assumes; else
  // every train moves.
  function advance(net: NonNullable<ShellState['net']>, dt: number, reset = false): void {
    view.sync();
    const flat = map.getBearing() === 0 && map.getPitch() === 0;
    const scale = 2 ** view.getZoom();
    const { x: w, y: h } = view.getSize();
    const o = view.getOrigin();
    const margin = Math.max(Render.CULL, 2 * VIEW_MARGIN * Math.max(w, h));
    const selectedId = state.selected?.type === 'train' ? state.selected.id : null;
    function near(t: TrackedTrain, meters: number): boolean {
      const pos = t.motion.pos;
      if (!pos || selectedId === t.id) return true;
      const p = Core.projectTrain(t, pos, view.project);
      const x = p.x * scale - o.x;
      const y = p.y * scale - o.y;
      const pad = margin + meters * p.metersToWorld * scale;
      return x >= -pad && y >= -pad && x <= w + pad && y <= h + pad;
    }
    options.onRemoved(advanceGpsTrains(net, live.trains, options.now(), dt, reset, flat ? near : undefined));
  }

  document.addEventListener('visibilitychange', () => {
    city.onHidden?.();
    map.stop();
    if (!document.hidden && state.net) {
      resetAnimation = true;
      advance(state.net, 0, true);
      // Resume at the current estimate without replaying a suspended pan.
      const pos = options.followed();
      if (pos) map.jumpTo({ center: [pos.lon, pos.lat] });
      renderer.resume();
      draw(0);
      city.onVisible?.();
    }
  });

  function install(): void {
    if (installed) return;
    installMapCanvasLayer(map, trainLayer);
    installed = true;
  }

  function start(): void {
    if (running) return;
    running = true;
    lastFrame = performance.now();
    draw(0);
  }

  return {
    map,
    view,
    renderer,
    hasBasemap,
    draw,
    install,
    start,
    running: () => running,
    resetMotion: () => {
      resetAnimation = true;
    },
  };
}
